import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Mail, ArrowLeft } from 'lucide-react';
import { authAPI } from '../services/api';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!orgCode.trim()) {
      setError('Organization code is required.');
      return;
    }
    if (!email.trim()) {
      setError('Email address is required.');
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword(email.trim(), orgCode.trim());
      setSuccess(true);
    } catch (err: any) {
      // Always show success message to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8 border border-surface-border">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <img
              src="/kelzix-logo.png"
              alt="KELZIX™"
              className="w-full max-w-[280px] sm:max-w-[320px] h-auto mx-auto mb-4"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <h1 className="text-2xl font-semibold text-content-primary mb-1">
                KELZIX<sup className="text-xs">&trade;</sup>
              </h1>
            </div>
            <h2 className="text-xl font-semibold text-content-primary mt-2">Forgot Password</h2>
            <p className="text-content-secondary text-sm mt-2">
              Enter your organization code and email to receive a password reset link.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
              If an account exists with this email, a password reset link has been sent. Please check your inbox.
            </div>
          )}

          {!success ? (
            <form onSubmit={handleSubmit}>
              {/* Organization Code */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Building2 size={16} className="inline mr-1" />
                  Organization Code
                </label>
                <input
                  type="text"
                  placeholder="Enter your organization code"
                  value={orgCode}
                  onChange={(e) => setOrgCode(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="input"
                  required
                  disabled={loading}
                />
              </div>

              {/* Email Address */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Mail size={16} className="inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="input"
                  required
                  disabled={loading}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive an email? Check your spam folder or try again.
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                  setOrgCode('');
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Try again
              </button>
            </div>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>

          {/* Divider and Admin Contact */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-400">OR</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              Contact your administrator to reset your password manually.
            </p>
          </div>

          {/* Trust Elements */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <span>Powered by</span>
              <span className="font-semibold text-gray-600">KELZIX</span>
            </div>
            <p className="text-center text-xs text-gray-400 mt-1">
              Your data is encrypted and secure
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          &copy; 2026 KELZIX&trade; Cold Chain Solutions
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
